import React, { Fragment } from "react";
import { Box } from "@mui/system";
import Illustration from "../../assets/images/illustrations/upload_illustration.svg";
import {
  Button,
  Stack,
  Typography,
  Container,
  Fade,
  Tooltip,
} from "@mui/material";
import theme, { DCButton, DCDashboardContainer } from "../../assets/theme/theme";
import Folder from "../DashboardGallery/Folder";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { IFolder, useDocumentContext } from "../../contexts/DocumentContext";
import ViewUploadDocsTitle from "../ViewUploadDocsTitle";
import { useDropzoneContext } from "../../contexts/DropzoneContext";
import Spinner from "../Spinner";
import CustomFolderSection from "../CustomFolderSection";
import { useUserContext } from "../../contexts/UserContext";
import { UploadDocumentModeSelectionModal } from "../Documents/UploadDocumentModeSelectionModal";
import { useAuth } from "../../contexts/AuthContext";
import { getResource } from "../../utils/fetchUtils";
import { CircularProgress } from "@mui/material";

export interface Props { }

const EmptyState: React.FC = () => {
  const { getInputProps } = useDropzoneContext();
  const [openModal, setOpenModal] = React.useState(false);

  const openUploadDocumentModeSelectionModal = () => {
    setOpenModal(true);
  };

  const closeUploadDocumentModeSelectionModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <div className="dashboard-root">
        <input {...getInputProps()} />
        <Box
          sx={{
            [theme.breakpoints.up('xs')]: {
              marginTop: '64px', mx: '25px', marginBottom: '0px'
            },
            [theme.breakpoints.up('sm')]: {
              marginTop: '64px', mx: '25px', marginBottom: '0px'
            },
            [theme.breakpoints.up('md')]: {
              marginTop: '64px', mx: '25px', marginBottom: '0px'
            },
            [theme.breakpoints.up('lg')]: {
              // backgroundColor: 'blue',
            },
          }}
          textAlign="center" mt={8} ml={4}>
          <img src={Illustration} alt="upload icon" className="Illustration-root" />
        </Box>
        <Box my={4} textAlign="center">
          <Typography variant="h3" component="h2">
            Are you ready to add your documents?
          </Typography>
          <Typography
            component="p"
            mt={2}
            fontSize={18}
            color={theme.palette.text.secondary}
          >
            Drag documents anywhere on the screen to upload
          </Typography>
          <Typography
            component="p"
            mt={2}
            fontSize={18}
            color={theme.palette.text.secondary}
          >
            OR
          </Typography>
          <Box textAlign="center" mt={2}>
            <Button variant="contained" component="span"
              onClick={() => {
                openUploadDocumentModeSelectionModal();
              }}>
              Upload
            </Button>
          </Box>
          <Typography component="p" mt={8} color={theme.palette.text.secondary}>
            Maximum document size should be <strong>20 MB</strong>. <br />
            Only <strong>PDF, PNG or JPEG</strong> formats are acceptable.
          </Typography>
          {/* <Box mt={4}>
                  <Button onClick={() => setShowFolders(true)}>
                    Show Folders
                  </Button>
                </Box> */}
        </Box>
        <UploadDocumentModeSelectionModal
          open={openModal}
          onClose={closeUploadDocumentModeSelectionModal}
        />
      </div>
    </>
  );
};

// DashboardGallery
// after the documents are fetched it shows either the folder view or empty state to upload documents
const DashboardGallery: React.FC<Props> = () => {
  const { documents, folders, initializedDocumentsAndFolders: initalizedDocumentsAndFolders, getDocDownloadUrl } =
    useDocumentContext();
  const { user } = useUserContext();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);

  function showFolders() {
    // if user has custom folders always showFolder irrespective of if they have documents or not
    if (user?.customFolders && user.customFolders.length > 0) {
      return true;
    }
    return initalizedDocumentsAndFolders && documents.length > 0;
  }

  if (!initalizedDocumentsAndFolders) return <Spinner />;

  const autoFolders = folders.filter((f) => !f.custom);

  async function exportAllDocuments(groups: IFolder[]) {
    setIsLoading(true);
    const zip = new JSZip();
    const token = await currentUser?.getIdToken();

    for (const group of groups) {
      const folderName = group.title.replace(/\s+/g, "_");
      const folder = zip.folder(folderName)!;

      // keep duplicates tracked *within this folder*
      const nameCounts: Record<string, number> = {};

      for (const doc of group.docs) {
        // choose best file name
        let baseName =
          doc.doc_name_autogenerated?.trim() ||
          doc.local_file_name ||
          "file.pdf";

        // ensure extension
        if (!baseName.includes(".")) {
          if (doc.doc_format === "image/png") {
            baseName += ".png";
          } else if (doc.doc_format === "image/jpeg") {
            baseName += ".jpg";
          } else {
            baseName += ".pdf";
          }
        }

        // check duplicates in this folder
        if (nameCounts[baseName]) {
          const ext = baseName.substring(baseName.lastIndexOf("."));
          const nameWithoutExt = baseName.substring(0, baseName.lastIndexOf("."));
          baseName = `${nameWithoutExt}_${nameCounts[baseName]}${ext}`;
          nameCounts[baseName] += 1;
        } else {
          nameCounts[baseName] = 1;
        }

        const downloadUrl = getDocDownloadUrl(doc);
        if (!downloadUrl) {
          console.error("No download URL for document", doc);
          continue;
        }
        const blob = await getResource(
          downloadUrl,
          {
            Authorization: `Bearer ${token}`,
          }
        );
        const data = await blob.arrayBuffer();
        folder.file(baseName, data, { binary: true });
      }
    }

    // get current date in this format 18Sept2024
    const date = new Date();
    const dateString = `${date.getDate()}${date.toLocaleString("default", {
      month: "short",
    })}${date.getFullYear()}`;
    const zipFileName = `immplify-${dateString}.zip`;
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, zipFileName);
    setIsLoading(false);
  }

  return (
    <Stack
      pb={10}
      bgcolor={theme.palette.common.white}
      sx={{ minHeight: "calc(100% - 64px)" }}
    >
      {showFolders() ? (
        <>
          <Box>
            <Container maxWidth={false} disableGutters>
              <Box sx={{ p: 4, pb: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }} className="document-gallery-title">
                <ViewUploadDocsTitle />
                <Tooltip title="Export your full document library for offline use">
                  <DCButton disabled={isLoading} onClick={async () => await exportAllDocuments(folders)} variant="contained" color="primary">
                    {isLoading ? <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}><CircularProgress size={16} color="inherit" /> Exporting Documents...</Box> : <span>Export All Documents</span>}
                  </DCButton>
                </Tooltip>
              </Box>
              <DCDashboardContainer className="auto-generated-main">
                {autoFolders.length > 0 && (
                  <Box mb={6}>
                    <Typography variant="h4" fontSize={18} fontWeight={500}>
                      Auto-generated Folders
                    </Typography>
                    <Box
                      className="auto-generated-model"
                      display="grid"
                      gridTemplateColumns="repeat(auto-fill,minmax(220px,1fr))"
                    >
                      {autoFolders.map((item, i) => {
                        return (
                          <Fade
                            className="auto-generated-folder"
                            in={!!item}
                            key={i}
                            {...(!!item ? { timeout: 150 * i + 1 } : {})}
                          >
                            <Box
                              display="flex"
                              alignItems="baseline"
                              justifyContent="center"
                              px={1}
                              pt={5}
                              key={"create-folder"}
                            >
                              <Box mx="auto" minHeight={196} className="auto-generated-folder-contains">
                                <Folder folder={item} />
                              </Box>
                            </Box>
                          </Fade>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                <CustomFolderSection />
              </DCDashboardContainer>
            </Container>
          </Box>
        </>
      ) : (
        <EmptyState />
      )}
    </Stack>
  );
};

export default DashboardGallery;
